import _ from 'lodash';
import { ChangeEvent, createRef, useState } from 'react';
import { useAppDispatch } from '../../../../../app/hooks';
import { RequireTier } from '../../../../auth/RequireTier';
import { RenderLog, RenderRegion, User } from '../../../../../services/UsersService';
import {
  setCustomMobileViewPort,
  setCustomMobileViewPortHeight,
  setCustomMobileViewPortWidth,
  setCustomViewPort,
  setCustomViewPortHeight,
  setCustomViewPortWidth,
  setEnableServiceWorker,
  setForceHTTPS,
  setLoadImages,
  setPageLoadTimeout,
  setParseShadowDom,
  setRestoreSearchParams,
  setEncodedBrackets,
  setFixHeadTag,
  setRemovePrefetch,
  setRemovePreload,
  setRetryTimedoutRenders,
  setRetryDirtyRenders,
  setRemoveStyleTags,
  setRemoveURLHash,
  setRenderRegion,
  setSkipCustomElementsForcePolyfill,
  setAddBlockedResources,
  setRemoveBlockedResources,
  setRenderLogSettings,
  setPreCheckStatus,
  setScrollSettings,
  setMinimumBodyLength,
  setSortQueryParameters,
  setScrollToBottomAfterPageLoad,
} from '../../UserSlice';

interface UserSettingsProps {
  readonly user: User;
}

// Render-log entries are time-boxed debug sessions (PRE-2964); internal-service clamps
// windows to 4 hours on save, so reject longer ones here with a visible message instead.
export const MAX_RENDER_LOG_WINDOW_MS = 4 * 60 * 60 * 1000;

// datetime-local inputs read their value as local time, so defaults built with
// toISOString() (UTC) would be shifted by the user's timezone offset.
const toDateTimeLocalValue = (epochMs: number): string => {
  const timezoneOffsetMs = new Date(epochMs).getTimezoneOffset() * 60 * 1000;
  return new Date(epochMs - timezoneOffsetMs).toISOString().slice(0, 19);
};

const UserSettings = (props: UserSettingsProps) => {
  const { user } = props;
  const dispatch = useAppDispatch();

  const addRemoveBlockedResourceInputRef = createRef<HTMLInputElement>();
  const addAddBlockedResourceInputRef = createRef<HTMLInputElement>();

  const addRenderLogSettingTypeRef = createRef<HTMLSelectElement>();
  const addRenderLogSettingFactorRef = createRef<HTMLSelectElement>();
  const addRenderLogSettingStartRef = createRef<HTMLInputElement>();
  const addRenderLogSettingEndRef = createRef<HTMLInputElement>();
  const [renderLogError, setRenderLogError] = useState<string | null>(null);

  const enableScrollRef = createRef<HTMLInputElement>();
  const scrollDistanceRef = createRef<HTMLInputElement>();
  const scrollDelayRef = createRef<HTMLInputElement>();
  const scrollRepeatDelayRef = createRef<HTMLInputElement>();
  const noOverscrollRef = createRef<HTMLInputElement>();
  const scrollSpeedRef = createRef<HTMLInputElement>();
  const scrollXRef = createRef<HTMLInputElement>();
  const scrollYRef = createRef<HTMLInputElement>();

  const changeForceHTTPS = (event: ChangeEvent) => {
    const checked = !user?.settings.forceHTTPS;
    dispatch(setForceHTTPS(checked));
  };

  const changeCustomViewPort = (event: ChangeEvent) => {
    const checked = !user?.settings.customViewPort;
    dispatch(setCustomViewPort(checked));
  };

  const changeCustomViewPortWidth = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setCustomViewPortWidth(parseInt(event.target.value, 10)));
  };

  const changeCustomViewPortHeight = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setCustomViewPortHeight(parseInt(event.target.value, 10)));
  };

  const changeCustomMobileViewPort = (event: ChangeEvent) => {
    const checked = !user?.settings.customMobileViewport;
    dispatch(setCustomMobileViewPort(checked));
  };

  const changeCustomMobileViewPortWidth = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setCustomMobileViewPortWidth(parseInt(event.target.value, 10)));
  };

  const changeCustomMobileViewPortHeight = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setCustomMobileViewPortHeight(parseInt(event.target.value, 10)));
  };

  const changeLoadImages = (event: ChangeEvent) => {
    const checked = !user?.settings.loadImages;
    dispatch(setLoadImages(checked));
  };

  const changeRenderRegion = (event: ChangeEvent<HTMLSelectElement>) => {
    dispatch(setRenderRegion(event.target.value as RenderRegion));
  };

  const changeSkipCustomElementsForcePolyfill = (event: ChangeEvent) => {
    const checked = !user?.settings.skipCustomElementsForcePolyfill;
    dispatch(setSkipCustomElementsForcePolyfill(checked));
  };

  const changeRemoveStyleTags = (event: ChangeEvent) => {
    const checked = !user?.settings.removeStyleTags;
    dispatch(setRemoveStyleTags(checked));
  };

  const changeRemoveURLHash = (event: ChangeEvent) => {
    const checked = !user?.settings.removeURLHash;
    dispatch(setRemoveURLHash(checked));
  };

  const changeEnableServiceWorker = (event: ChangeEvent) => {
    const checked = !user?.settings.enableServiceWorker;
    dispatch(setEnableServiceWorker(checked));
  };

  const changePageLoadTimeout = (event: ChangeEvent<HTMLInputElement>) => {
    dispatch(setPageLoadTimeout(parseInt(event.target.value, 10)));
  };

  const changeParseShadowDom = (event: ChangeEvent) => {
    const checked = !user?.settings.parseShadowDom;
    dispatch(setParseShadowDom(checked));
  };

  const changeRestoreSearchParams = () => {
    const checked = !user?.settings?.normalizeUri?.restoreSearchParams;
    dispatch(setRestoreSearchParams(checked));
  };

  const changeEncodedBrackets = () => {
    const checked = !user?.settings?.normalizeUri?.encodedBrackets;
    dispatch(setEncodedBrackets(checked));
  };

  const changeFixHead = () => {
    const checked = !user?.settings?.fixHeadTag;
    dispatch(setFixHeadTag(checked));
  };

  const changeRemovePrefetch = () => {
    const checked = !user?.settings.removePrefetch;
    dispatch(setRemovePrefetch(checked));
  };

  const changeRemovePreload = () => {
    const checked = !user?.settings.removePreload;
    dispatch(setRemovePreload(checked));
  };

  const changeRetryTimedoutRenders = () => {
    const checked = !user?.settings.retryTimedoutRenders;
    dispatch(setRetryTimedoutRenders(checked));
  };

  const changeRetryDirtyRenders = () => {
    const checked = !user?.settings.retryDirtyRenders;
    dispatch(setRetryDirtyRenders(checked));
  };

  const changePreCheckStatus = () => {
    const checked = !user?.settings.preCheckStatus;
    dispatch(setPreCheckStatus(checked));
  };

  const removeRemoveBlockedResource = (resource: string) => {
    const removeBlockedResources = user?.settings.removeBlockedResources.filter((r) => r !== resource);
    dispatch(setRemoveBlockedResources(removeBlockedResources));
  };

  const changeSortQueryParameters = () => {
    const checked = !user?.settings?.normalizeUri?.sortQueryParameters;
    dispatch(setSortQueryParameters(checked));
  };

  const changeScrollToBottomAfterPageLoad = () => {
    const checked = !user?.settings?.scrollToBottomAfterPageLoad;
    dispatch(setScrollToBottomAfterPageLoad(checked));
  };

  const addRemoveBlockedResource = (resource: string) => {
    if (!resource) {
      return;
    }
    if (user?.settings.removeBlockedResources.some((r) => r === resource)) {
      return;
    }
    const removeBlockedResources = [...user?.settings.removeBlockedResources, resource];
    dispatch(setRemoveBlockedResources(removeBlockedResources));
  };

  const removeAddBlockedResource = (resource: string) => {
    const addBlockedResources = user?.settings.addBlockedResources.filter((r) => r !== resource);
    dispatch(setAddBlockedResources(addBlockedResources));
  };

  const addAddBlockedResource = (resource: string) => {
    if (!resource) {
      return;
    }
    if (user?.settings.addBlockedResources.some((r) => r === resource)) {
      return;
    }
    const addBlockedResources = [...user?.settings.addBlockedResources, resource];
    dispatch(setAddBlockedResources(addBlockedResources));
  };

  const addRenderLogSetting = () => {
    const type = addRenderLogSettingTypeRef.current?.value || 'db';
    const factor = addRenderLogSettingFactorRef.current?.value || 'log';
    const startMs = new Date(addRenderLogSettingStartRef.current?.value || Date.now()).getTime();
    const endMs = new Date(addRenderLogSettingEndRef.current?.value || Date.now()).getTime();
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) {
      setRenderLogError('Start and end must be valid dates.');
      return;
    }
    if (endMs <= startMs) {
      setRenderLogError('End must be after start.');
      return;
    }
    if (endMs - startMs > MAX_RENDER_LOG_WINDOW_MS) {
      setRenderLogError('A render log window can be at most 4 hours.');
      return;
    }
    setRenderLogError(null);
    const renderLogs = [
      ...user.settings.renderLogs,
      {
        type,
        factor,
        start: new Date(startMs).toISOString(),
        end: new Date(endMs).toISOString(),
      },
    ];
    dispatch(setRenderLogSettings(renderLogs));
  };

  const removeRenderLogSetting = (renderLog: RenderLog) => {
    const renderLogs = user.settings.renderLogs.filter((r) => !_.isEqual(r, renderLog));
    dispatch(setRenderLogSettings(renderLogs));
  };

  const changeScrollSettings = () => {
    const enabled = enableScrollRef.current?.checked || false;
    const scrollDistance = parseInt(scrollDistanceRef.current?.value || '-100');
    const scrollDelay = parseInt(scrollDelayRef.current?.value || '0');
    const scrollRepeatDelay = parseInt(scrollRepeatDelayRef.current?.value || '1000');
    const noOverscroll = noOverscrollRef.current?.checked || false;
    const scrollSpeed = parseInt(scrollSpeedRef.current?.value || '800');
    const scrollX = parseInt(scrollXRef.current?.value || 'NaN') || undefined;
    const scrollY = parseInt(scrollYRef.current?.value || 'NaN') || undefined;
    dispatch(
      setScrollSettings({
        x: scrollX,
        y: scrollY,
        delay: scrollDelay,
        speed: scrollSpeed,
        enabled,
        distance: scrollDistance,
        repeatDelay: scrollRepeatDelay,
        noOverscroll,
      })
    );
  };

  const changeMinimumBodyLength = (event: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value) || -1;
    dispatch(setMinimumBodyLength(value));
  };

  return (
    <RequireTier requiredTier="admin" policy="disable">
      <fieldset className="border-0 p-0 m-0">
      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="forceHTTPS" className="form-label">
                force HTTPS
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="forceHTTPS"
                checked={user.settings.forceHTTPS}
                onChange={changeForceHTTPS}
              />
              {user.settings.forceHTTPS && (
                <span className="text-danger ms-3">
                  All URLs using the <b>http</b> protocol will be <b>removed</b> and replaced with the same URLs using
                  the <b>https</b> protocol.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="form-group">
        <div className="form-check form-switch">
          <label htmlFor="enableCustomViewport" className="form-label">
            custom viewport
          </label>
          <input
            type="checkbox"
            className="form-check-input"
            id="enableCustomViewport"
            checked={user.settings.customViewPort !== null}
            onChange={changeCustomViewPort}
          />
        </div>
      </div>
      {user.settings.customViewPort && (
        <div className="row align-items-center">
          <div className="col">
            <div className="form-group">
              <label htmlFor="customViewPortWidth" className="form-label">
                width
              </label>
              <input
                type="number"
                className="form-control"
                id="customViewPortWidth"
                value={user.settings.customViewPort.width}
                onChange={changeCustomViewPortWidth}
              />
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <label htmlFor="customViewPortHeight" className="form-label">
                height
              </label>
              <input
                type="number"
                className="form-control"
                id="customViewPortHeight"
                value={user.settings.customViewPort.height}
                onChange={changeCustomViewPortHeight}
              />
            </div>
          </div>
        </div>
      )}

      <div className="form-group">
        <div className="form-check form-switch">
          <label htmlFor="enableCustomMobileViewport" className="form-label">
            custom mobile viewport
          </label>
          <input
            type="checkbox"
            className="form-check-input"
            id="enableCustomMobileViewport"
            checked={user.settings.customMobileViewport !== null}
            onChange={changeCustomMobileViewPort}
          />
        </div>
      </div>
      {user.settings.customMobileViewport && (
        <div className="row align-items-center">
          <div className="col">
            <div className="form-group">
              <label htmlFor="customMobileViewPortWidth" className="form-label">
                width
              </label>
              <input
                type="number"
                className="form-control"
                id="customMobileViewPortWidth"
                value={user.settings.customMobileViewport.width}
                onChange={changeCustomMobileViewPortWidth}
              />
            </div>
          </div>
          <div className="col">
            <div className="form-group">
              <label htmlFor="customMobileViewPortHeight" className="form-label">
                height
              </label>
              <input
                type="number"
                className="form-control"
                id="customMobileViewPortHeight"
                value={user.settings.customMobileViewport.height}
                onChange={changeCustomMobileViewPortHeight}
              />
            </div>
          </div>
        </div>
      )}

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="loadImages" className="form-label">
                load images
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="loadImages"
                checked={user.settings.loadImages}
                onChange={changeLoadImages}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <select
            className="form-select mb-3"
            data-choices
            value={user.settings.renderRegion}
            onChange={changeRenderRegion}
          >
            <option>{'us'}</option>
            <option>{'eu'}</option>
            <option>{'shared'}</option>
          </select>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="skipCustomElementsForcePolyfill" className="form-label">
                skip custom elements force polyfill
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="skipCustomElementsForcePolyfill"
                checked={user.settings.skipCustomElementsForcePolyfill}
                onChange={changeSkipCustomElementsForcePolyfill}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="removeStyleTags" className="form-label">
                remove style tags
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="removeStyleTags"
                checked={user.settings.removeStyleTags}
                onChange={changeRemoveStyleTags}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="removeURLHash" className="form-label">
                remove URL hash
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="removeURLHash"
                checked={user.settings.removeURLHash}
                onChange={changeRemoveURLHash}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="enableServiceWorker" className="form-label">
                enable service worker
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="enableServiceWorker"
                checked={user.settings.enableServiceWorker}
                onChange={changeEnableServiceWorker}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <label htmlFor="pageLoadTimeout" className="form-label">
              page load timeout
            </label>
            <input
              type="number"
              className="form-control"
              id="pageLoadTimeout"
              value={user.settings.pageLoadTimeout}
              onChange={changePageLoadTimeout}
            />
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="parseShadowDom" className="form-label">
                parse shadow dom
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="parseShadowDom"
                checked={user.settings.parseShadowDom}
                onChange={changeParseShadowDom}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="restoreSearchParams" className="form-label">
                restore search params
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="restoreSearchParams"
                checked={user.settings.normalizeUri?.restoreSearchParams}
                onChange={changeRestoreSearchParams}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="sortQueryParameters" className="form-label">
                sort query params
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="sortQueryParameters"
                checked={user.settings.normalizeUri?.sortQueryParameters}
                onChange={changeSortQueryParameters}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="encodedBrackets" className="form-label">
                Encoded brackets
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="encodedBrackets"
                checked={user.settings.normalizeUri?.encodedBrackets}
                onChange={changeEncodedBrackets}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="encodedBrackets" className="form-label">
                Head tag: move invalid tags (divs) to the end
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="fixHeadTag"
                checked={user.settings.fixHeadTag}
                onChange={changeFixHead}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="removePrefetch" className="form-label">
                remove prefetch tags
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="removePrefetch"
                checked={user.settings.removePrefetch}
                onChange={changeRemovePrefetch}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="removePreload" className="form-label">
                remove preload tags
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="removePreload"
                checked={user.settings.removePreload}
                onChange={changeRemovePreload}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="retryTimedoutRenders" className="form-label">
                retry timed out renders
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="retryTimedoutRenders"
                checked={user.settings.retryTimedoutRenders}
                onChange={changeRetryTimedoutRenders}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="retryDirtyRenders" className="form-label">
                retry dirty renders
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="retryDirtyRenders"
                checked={user.settings.retryDirtyRenders}
                onChange={changeRetryDirtyRenders}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="preCheckStatus" className="form-label">
                pre check - check HTTP status before render
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="preCheckStatus"
                checked={user.settings.preCheckStatus}
                onChange={changePreCheckStatus}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="form-group">
        <div>
          <label>Add blocked resources</label>
        </div>
        <div className="pl-3">
          <ul className="list-group">
            {user.settings.addBlockedResources.map((r) => (
              <li id={`add-blocked-resource-${r}`} className="list-group-item">
                {r}
                <button onClick={() => removeAddBlockedResource(r)} className="btn btn-primary float-right">
                  <i className="fe fe-trash"></i>
                </button>
              </li>
            ))}
            <li id="remove-blocked-resource-new" className="list-group-item">
              <div className="row">
                <div className="col-10">
                  <input className="form-control" ref={addAddBlockedResourceInputRef} />
                </div>
                <div className="col-2">
                  <button
                    onClick={() => addAddBlockedResource(addAddBlockedResourceInputRef.current?.value || '')}
                    className="btn btn-primary float-right"
                  >
                    <i className="fe fe-plus"></i>
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <hr className="my-5" />
      <div className="form-group">
        <div>
          <label>Remove blocked resources</label>
        </div>
        <div className="pl-3">
          <ul className="list-group">
            {user.settings.removeBlockedResources.map((r) => (
              <li id={`remove-blocked-resource-${r}`} className="list-group-item">
                {r}
                <button onClick={() => removeRemoveBlockedResource(r)} className="btn btn-primary float-right">
                  <i className="fe fe-trash"></i>
                </button>
              </li>
            ))}
            <li id="remove-blocked-resource-new" className="list-group-item">
              <div className="row">
                <div className="col-10">
                  <input className="form-control" ref={addRemoveBlockedResourceInputRef} />
                </div>
                <div className="col-2">
                  <button
                    onClick={() => addRemoveBlockedResource(addRemoveBlockedResourceInputRef.current?.value || '')}
                    className="btn btn-primary float-right"
                  >
                    <i className="fe fe-plus"></i>
                  </button>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <hr className="my-5" />
      <div className="form-group">
        <div>
          <label>Render log settings</label>
        </div>
        <div className="pl-3">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Type</th>
                <th scope="col">Factor</th>
                <th scope="col">Start</th>
                <th scope="col">End</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {user.settings.renderLogs.map((r) => (
                <tr key={`${r.type}-${r.factor}-${r.start}-${r.end}`}>
                  <td>{r.type}</td>
                  <td>{r.factor}</td>
                  <td>{r.start}</td>
                  <td>{r.end}</td>
                  <td>
                    <button onClick={() => removeRenderLogSetting(r)} className="btn btn-primary">
                      <i className="fe fe-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
              <tr key="new-render-log-setting">
                <td>
                  <select className="form-control" ref={addRenderLogSettingTypeRef}>
                    <option value="db">db</option>
                  </select>
                </td>
                <td>
                  <select className="form-control" ref={addRenderLogSettingFactorRef}>
                    <option value="log">log</option>
                    <option value="long_requests"> long_requests</option>
                    <option value="5xx">5xx</option>
                  </select>
                </td>
                <td>
                  <input
                    className="form-control"
                    type="datetime-local"
                    defaultValue={toDateTimeLocalValue(Date.now())}
                    ref={addRenderLogSettingStartRef}
                  />
                </td>
                <td>
                  <input
                    className="form-control"
                    type="datetime-local"
                    defaultValue={toDateTimeLocalValue(Date.now() + 2 * 60 * 60 * 1000)}
                    ref={addRenderLogSettingEndRef}
                  />
                </td>
                <td>
                  <button
                    onClick={() => {
                      addRenderLogSetting();
                    }}
                    className="btn btn-primary"
                  >
                    <i className="fe fe-plus"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
          {renderLogError && <div className="alert alert-danger py-1">{renderLogError}</div>}
        </div>
      </div>
      <hr className="my-5" />
      <div>
        <div className="row">
          <div className="col">Scroll during render:</div>
        </div>
        <div className="row">
          <div className="form-group">
            <div className="col form-check form-switch">
              <label htmlFor="enableScroll" className="form-label">
                Enable scroll during render
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="enableScroll"
                checked={user.settings?.scroll?.enabled || false}
                onChange={changeScrollSettings}
                ref={enableScrollRef}
              />
            </div>
          </div>
          {user.settings.scroll?.enabled && (
            <div className="col">
              <div className="row">
                <div className="col">
                  <label htmlFor="scrollDistance" className="form-label">
                    Scroll distance
                  </label>
                </div>
                <div className="col">
                  <input
                    type="number"
                    defaultValue="-100"
                    className="form-control"
                    id="scrollDistance"
                    onChange={changeScrollSettings}
                    ref={scrollDistanceRef}
                    value={user.settings?.scroll?.distance}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <label htmlFor="scrollDelay" className="form-label">
                    Scroll delay
                  </label>
                </div>
                <div className="col">
                  <input
                    type="number"
                    defaultValue="0"
                    className="form-control"
                    id="scrollDelay"
                    onChange={changeScrollSettings}
                    ref={scrollDelayRef}
                    value={user.settings?.scroll?.delay}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <label htmlFor="scrollRepeatDelay" className="form-label">
                    Repeat delay
                  </label>
                </div>
                <div className="col">
                  <input
                    type="number"
                    defaultValue="1000"
                    className="form-control"
                    id="scrollRepeatDelay"
                    onChange={changeScrollSettings}
                    ref={scrollRepeatDelayRef}
                    value={user.settings?.scroll?.repeatDelay}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <label htmlFor="noOverscroll" className="form-label">
                    On overscroll
                  </label>
                </div>
                <div className="col">
                  <div className="form-check form-switch">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="noOverscroll"
                      onChange={changeScrollSettings}
                      ref={noOverscrollRef}
                      checked={user.settings?.scroll?.noOverscroll || false}
                    />
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <label htmlFor="scrollSpeed" className="form-label">
                    Scroll speed
                  </label>
                </div>
                <div className="col">
                  <input
                    type="number"
                    defaultValue="800"
                    className="form-control"
                    id="scrollSpeed"
                    onChange={changeScrollSettings}
                    ref={scrollSpeedRef}
                    value={user.settings?.scroll?.speed}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <label htmlFor="scrollX" className="form-label">
                    Cursor location x (empty - width/2)
                  </label>
                </div>
                <div className="col">
                  <input
                    type="number"
                    className="form-control"
                    id="scrollX"
                    onChange={changeScrollSettings}
                    ref={scrollXRef}
                    value={user.settings?.scroll?.x}
                  />
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <label htmlFor="scrollY" className="form-label">
                    Cursor location y (empty - height/2)
                  </label>
                </div>
                <div className="col">
                  <input
                    type="number"
                    className="form-control"
                    id="scrollY"
                    onChange={changeScrollSettings}
                    ref={scrollYRef}
                    value={user.settings?.scroll?.y}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <label htmlFor="minimumBodyLength" className="form-label">
              Minimum body length (characters)
            </label>
            <input
              type="number"
              className="form-control"
              id="minimumBodyLength"
              value={user.environment.minimumBodyLength}
              onChange={changeMinimumBodyLength}
            />
          </div>
        </div>
      </div>
      <div className="row align-items-center">
        <div className="col">
          <div className="form-group">
            <div className="form-check form-switch">
              <label htmlFor="scrollToBottomAfterPageLoad" className="form-label">
                Scroll to bottom after page load
              </label>
              <input
                type="checkbox"
                className="form-check-input"
                id="scrollToBottomAfterPageLoad"
                checked={user.settings.scrollToBottomAfterPageLoad}
                onChange={changeScrollToBottomAfterPageLoad}
              />
            </div>
          </div>
        </div>
      </div>

      <hr className="my-5" />
      </fieldset>
    </RequireTier>
  );
};

export default UserSettings;
